import { getOrgIdFromToken, getUserIdFromToken } from "@/api/utils";
import { QUERY_CACHE_GC_TIME_MS, queryClient } from "@/queryClient";
import { useAuth0 } from "@auth0/auth0-react";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { defaultShouldDehydrateQuery, type Query } from "@tanstack/react-query";
import {
  removeOldestQuery,
  type PersistedClient,
  type PersistQueryClientOptions,
} from "@tanstack/react-query-persist-client";
import { useEffect, useState, type ReactNode } from "react";

export const QUERY_CACHE_STORAGE_KEY = "COHESIVE_QUERY_CACHE";
export const QUERY_CACHE_IDENTITY_KEY = "COHESIVE_QUERY_CACHE_IDENTITY";

/**
 * Bump this whenever the shape of persisted query data changes in a way that
 * would break consumers of restored caches (e.g. renamed query keys, changed
 * API response envelopes, redaction rules). Any mismatch throws the whole
 * persisted cache away.
 */
// v6 removes field-level presigned-URL redaction (and its `_persistRedacted`
// markers) entirely. Redaction only governed writes, so without a bump a v5
// snapshot would keep hydrating redacted, marker-carrying rows nothing
// recognizes anymore.
// v7: workspace/canvas/file responses gained capability fields; persisted
// pre-capability caches read as "allowed" and must not survive the upgrade.
// v8 strips presigned URLs on restore. Snapshots written under v6/v7 hold
// hour-old presigns that CloudFront answers with 403; discard them rather
// than wait for the first restore to strip them.
const QUERY_CACHE_BUSTER = "v8";

/**
 * Query-key segments whose data must never be written to localStorage:
 * short-lived credentials (TipTap/collab JWTs, fetched presigned bodies),
 * Stripe/billing payloads, and `/me`.
 *
 * `/me` is session state the router reads, not view data: `App` sends the user
 * to create-organization on `organizations: []` and blocks rendering while it
 * loads. Restoring it means a snapshot taken seconds earlier — before signup
 * finished joining an org, before a plan changed, before permissions were
 * granted — decides routing for the new session. There is nothing to gain
 * either; the app renders nothing until `/me` resolves regardless.
 */
const NEVER_PERSIST_KEY_SEGMENTS = new Set<string>([
  "tiptapToken",
  "collaborationToken",
  "presignedContent",
  "setupIntentClientSecret",
  "paymentMethods",
  "subscription",
  "stripeInvoices",
  "creditUsagePerUser",
  "userInfo",
  // Access answers must come from a live check every session: the
  // ["permission", ...] fan-out entries carry gcTime Infinity with no queryFn
  // and no observer, so a restored copy of a since-revoked permission would
  // never correct itself.
  "permission",
  "checkBulkPermissions",
]);

/**
 * Whether a query is safe to persist to localStorage. Sensitive/ephemeral
 * keys are denylisted, and individual queries can opt out with
 * `meta: { persist: false }`.
 *
 * Queries that opt out are also the ones allowed to set a `gcTime` shorter
 * than `maxAge` — see the invariant documented on `QUERY_CACHE_GC_TIME_MS`.
 */
export function shouldPersistQuery(
  query: Pick<Query, "queryKey" | "meta">,
): boolean {
  if (query.meta?.persist === false) {
    return false;
  }
  return !query.queryKey.some(
    (segment) =>
      typeof segment === "string" && NEVER_PERSIST_KEY_SEGMENTS.has(segment),
  );
}

/**
 * Presigned URL / upload credential fields, dropped from every restored
 * payload.
 *
 * Presigns die after an hour (`DefaultExpires` in the backend's CloudFront
 * signer) and the snapshot lives for `maxAge` — a day — so any URL coming off
 * disk is assumed dead. Consumers that render one get a 403 from CloudFront
 * and, having no reason to expect a signed URL to fail, never retry.
 *
 * Matched by field name at any depth, because the same payloads travel under
 * many keys: file records appear under `fileRecord(s)` but also
 * `["archived", "files"]`, search results and the workspace tree.
 *
 * `url` is deliberately included even though it is a generic name — in this
 * API it is almost always an S3 presign (file records, skill files, message
 * attachments). The rare plain link caught by it (web-search citations) is
 * restored by the refetch stripping already forces.
 */
const PRESIGNED_FIELDS = new Set<string>([
  "url",
  "download_url",
  "preview_urls",
  "upload_url",
  "upload_field_values",
  "hero_image_url",
  "hero_image_light_url",
  "hero_image_dark_url",
]);

/**
 * Recursively drop presign fields from a value, reporting whether any were
 * removed.
 */
function dropPresignedFields(data: unknown): {
  data: unknown;
  stripped: boolean;
} {
  if (Array.isArray(data)) {
    let stripped = false;
    const next = data.map((item) => {
      const result = dropPresignedFields(item);
      stripped = stripped || result.stripped;
      return result.data;
    });
    return { data: next, stripped };
  }
  if (data && typeof data === "object") {
    let stripped = false;
    const next: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(
      data as Record<string, unknown>,
    )) {
      if (PRESIGNED_FIELDS.has(key)) {
        stripped = true;
        continue;
      }
      const result = dropPresignedFields(value);
      stripped = stripped || result.stripped;
      next[key] = result.data;
    }
    return { data: next, stripped };
  }
  return { data, stripped: false };
}

/**
 * Strip presigned URLs out of a snapshot as it is read back off disk.
 *
 * Stripping on restore rather than on write keeps the live cache — which
 * holds URLs that actually work — untouched for the whole session, so no
 * consumer ever has to tell a redacted row from a real one.
 *
 * A stripped payload can no longer render media, so it must not restore as
 * *fresh*: `useFileRecord` holds a 4-minute `staleTime`, and a reload inside
 * that window would otherwise leave thumbnails and previews blank with
 * nothing scheduled to heal them. `isInvalidated` makes the restored query
 * stale regardless of `staleTime`, so React Query refetches as soon as an
 * observer mounts while still serving the cached metadata (path, size,
 * status, tree structure) immediately.
 *
 * Only queries that actually lost a field are marked, so the ones carrying no
 * presigned data still restore fresh and keep the instant-load win. That is
 * why marking cannot be the only thing that settles a restored row: a payload
 * the server never gave a `url` — its placeholder for a dead reference — is
 * indistinguishable here from one that simply carries no media, and neither
 * loses a field. Consumers that need a settled answer ask for one themselves
 * (see `isRestoredQueryData`).
 *
 * `dataUpdatedAt` is deliberately left untouched: `removeOldestQuery` sorts
 * by it when evicting on quota overflow, and zeroing it would make
 * file-record entries the first casualties every time.
 */
export function stripPersistedClient(client: PersistedClient): PersistedClient {
  return {
    ...client,
    clientState: {
      ...client.clientState,
      queries: client.clientState.queries.map((query) => {
        const { data, stripped } = dropPresignedFields(query.state.data);
        if (!stripped) return query;
        return {
          ...query,
          state: { ...query.state, data, isInvalidated: true },
        };
      }),
    },
  };
}

/** When the snapshot was read back in; 0 until one is. */
let restoredAtMs = 0;

/**
 * Whether query data of this age came off disk instead of the network:
 * everything the snapshot carried predates the restore, everything fetched
 * (or `setQueryData`-ed from a server response) since does not.
 *
 * Restored file-record rows have lost their presigned URLs, which leaves them
 * indistinguishable on shape from a server payload that genuinely has none —
 * a dead reference. Callers that read meaning into a missing `url` have to
 * wait for a fresh answer.
 *
 * Waiting is on the caller to end. Stripping marks the rows it touched
 * `isInvalidated`, so those refetch on mount, but a row that lost nothing
 * restores fresh and would sit unread for its whole `staleTime` — so a caller
 * that suppresses a verdict on this must also drive one refetch to lift it
 * (`useHydrateFileRecordEntity` does). Deliberately generic: the persister
 * knows about presign fields, not about what any payload shape means.
 */
export function isRestoredQueryData(dataUpdatedAt: number): boolean {
  return restoredAtMs > 0 && dataUpdatedAt < restoredAtMs;
}

function readPersistedClient(cached: string): PersistedClient {
  restoredAtMs = Date.now();
  return stripPersistedClient(JSON.parse(cached) as PersistedClient);
}

const persister = createSyncStoragePersister({
  storage: typeof window === "undefined" ? undefined : window.localStorage,
  key: QUERY_CACHE_STORAGE_KEY,
  deserialize: readPersistedClient,
  // On quota overflow, evict the oldest queries until the snapshot fits.
  retry: removeOldestQuery,
});

export const persistOptions: Omit<PersistQueryClientOptions, "queryClient"> = {
  persister,
  // Keep in lockstep with `QUERY_CACHE_GC_TIME_MS` — see queryClient.ts.
  maxAge: QUERY_CACHE_GC_TIME_MS,
  buster: QUERY_CACHE_BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) =>
      defaultShouldDehydrateQuery(query) && shouldPersistQuery(query),
    // Mutations have no registered defaults to resume from after a reload,
    // so persisting them would only produce dead entries.
    shouldDehydrateMutation: () => false,
  },
};

/** Drops the persisted query cache and its identity marker from localStorage. */
export function clearPersistedQueryCache(): void {
  localStorage.removeItem(QUERY_CACHE_STORAGE_KEY);
  localStorage.removeItem(QUERY_CACHE_IDENTITY_KEY);
}

/**
 * Wipe both the in-memory and persisted React Query caches. Call this before
 * every Auth0 `logout()` so the next browser session cannot restore this
 * user's data.
 */
export function clearSessionQueryCaches(): void {
  clearPersistedQueryCache();
  queryClient.clear();
}

/** Recorded for a token whose `c_org_id` claim is absent. */
const NO_ORG_IDENTITY = "orgless";

/**
 * Recorded when the identity check itself failed, so the cache the session
 * goes on to write has no verified owner.
 *
 * Real identities are always `orgId:userId`, so this can never match one and
 * the next check is guaranteed to wipe. Leaving the marker absent instead
 * would read as a fresh login on the next load, handing whatever the
 * unverified session persisted to the next user of the browser.
 */
const UNVERIFIED_IDENTITY = "unverified";

/**
 * Owner of the cache for `token`, as `orgId:userId`.
 *
 * `getOrgIdFromToken` throws when the token carries no `c_org_id`, which is
 * the normal state between signup and joining an org. That is a real identity,
 * not a failure, and it has to be recorded: leaving the marker unwritten makes
 * the org-scoped re-auth after create-organization look like a fresh login, so
 * the org-less snapshot survives and `/me` restores with `organizations: []` —
 * bouncing the user straight back to the create-organization page.
 */
function cacheIdentity(token: string): string {
  let orgId: string;
  try {
    orgId = getOrgIdFromToken(token);
  } catch {
    orgId = NO_ORG_IDENTITY;
  }
  return `${orgId}:${getUserIdFromToken(token)}`;
}

/**
 * The persisted cache is restored before Auth0 resolves, so it may belong to
 * a different user/org (shared browser, org switch). Children are gated until
 * the token's `orgId:userId` is checked against the identity recorded with
 * the cache — mirroring `OpenTabsContext` — so restored query data is never
 * shown for the wrong account.
 */
export function QueryCacheIdentityGuard({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [identityReady, setIdentityReady] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      setIdentityReady(true);
      return;
    }

    let cancelled = false;
    setIdentityReady(false);
    getAccessTokenSilently()
      .then((token) => {
        if (cancelled) return;
        const identity = cacheIdentity(token);
        const stored = localStorage.getItem(QUERY_CACHE_IDENTITY_KEY);
        // Only wipe when a *different* identity owned the cache. A missing
        // marker (fresh login after logout) must not clear queries that
        // already fetched between auth resolving and this check finishing.
        if (stored !== null && stored !== identity) {
          clearPersistedQueryCache();
          queryClient.clear();
        }
        localStorage.setItem(QUERY_CACHE_IDENTITY_KEY, identity);
        setIdentityReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        // Failed identity check — don't risk showing another user's data.
        clearPersistedQueryCache();
        queryClient.clear();
        localStorage.setItem(QUERY_CACHE_IDENTITY_KEY, UNVERIFIED_IDENTITY);
        setIdentityReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  if (isLoading || (isAuthenticated && !identityReady)) {
    return null;
  }

  return children;
}
